import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import uuid from "react-uuid";
import {
	Button,
	Card,
	CardBody,
	CardHeader,
	Col,
	Row,
	Input,
	FormGroup,
	Label,
	Alert,
	Badge,
} from "reactstrap";
import { usePlaidLink } from "react-plaid-link";
import PlaidLogo from "assets/img/Plaid_logo.svg";
import {
	createIdentityVerification,
	getIdentityVerificationStatus,
	getIdentityVerificationStatusByContact,
	createLinkToken,
	exchangePublicToken,
	saveBankVerification,
	checkFundsByContact,
	createShareableBankLink,
} from "api/PlaidAPI";
import { sendTwilioMessage } from "api/TwilioApi";

/**
 * PlaidModal component
 * Manages Plaid Identity Verification (KYC) and Bank Account Linking/Fund Checking
 *
 * @param {Object} props - Component props
 * @param {Object} props.buyer - Buyer object with contact information
 * @param {string} props.dealerID - Dealer ID
 * @param {string} props.locationID - Location ID
 * @param {Object} props.buyerAddress - Buyer address object
 * @param {boolean} props.usePlaid - Whether dealership has Plaid premium feature enabled
 * @returns {JSX.Element} The PlaidModal component
 */
const PlaidModal = ({
	buyer,
	dealerID,
	locationID,
	buyerAddress,
	usePlaid,
}) => {
	// Identity Verification state
	const [idvStatus, setIdvStatus] = useState(null);
	const [idvUrl, setIdvUrl] = useState(null);
	const [idvVerificationId, setIdvVerificationId] = useState(null);
	const [idvLoading, setIdvLoading] = useState(false);
	const [idvError, setIdvError] = useState(null);
	const [idvResults, setIdvResults] = useState(null);
	const [smsSending, setSmsSending] = useState(false);
	const [smsSuccess, setSmsSuccess] = useState(false);

	// Bank Account Linking state
	const [bankLinkSending, setBankLinkSending] = useState(false);
	const [bankLinkSuccess, setBankLinkSuccess] = useState(false);

	// Helper function to save verification data to sessionStorage
	const saveVerificationToStorage = (
		verificationId,
		url,
		status,
		smsSent = false
	) => {
		if (buyer?.ID) {
			const storageKey = `plaid_verification_${buyer.ID}`;
			const data = { verificationId, url, status, smsSent };
			sessionStorage.setItem(storageKey, JSON.stringify(data));
		}
	};

	// Load verification ID from sessionStorage on mount and check latest status
	useEffect(() => {
		if (buyer?.ID && dealerID && locationID) {
			const storageKey = `plaid_verification_${buyer.ID}`;
			const stored = sessionStorage.getItem(storageKey);

			// Load from storage first
			if (stored) {
				try {
					const { verificationId, url, status, smsSent } = JSON.parse(stored);
					setIdvVerificationId(verificationId);
					setIdvUrl(url);
					setIdvStatus(status);

					// Restore SMS success state from storage
					if (smsSent === true) {
						setSmsSuccess(true);
					}

					// Backward compatibility: If status is 'sent', mark SMS as already successful
					if (status === "sent") {
						setSmsSuccess(true);
					}

					// Only check for latest status from server if we have a verificationId
					// (i.e., a verification was already created)
					if (verificationId) {
						checkStatusFromServer(verificationId);
					}
				} catch (error) {
					console.error("Error parsing stored verification data:", error);
				}
			}
		}

		// Helper function to check status from server
		async function checkStatusFromServer(existingVerificationId) {
			try {
				const dcDealerID =
					String(dealerID).padStart(4, "0") +
					String(locationID).padStart(4, "0");
				const response = await getIdentityVerificationStatusByContact(
					dcDealerID,
					String(buyer.ID)
				);

				if (response.data && response.data.identity_verification_id) {
					// We found an existing verification
					const verificationId = response.data.identity_verification_id;
					const shareableUrl = response.data.shareable_url;
					const kycStatus = response.data.kyc_check?.status;
					const apiStatus = response.data.status;

					// Determine status
					let newStatus;
					if (kycStatus === "success" && apiStatus === "success") {
						newStatus = "verified";
					} else if (apiStatus === "failed") {
						newStatus = "failed";
					} else {
						// For active, pending, or any other in-progress status, use the actual API status
						// This ensures "active" stays "active" to show the "Send Verification" UI
						newStatus = apiStatus || "unverified";
					}

					// Update state
					setIdvVerificationId(verificationId);
					setIdvStatus(newStatus);
					setIdvResults(response.data);

					// Only set URL if verification is still pending/active
					if (shareableUrl && newStatus !== "verified") {
						setIdvUrl(shareableUrl);
					}

					// Save to storage (preserve existing smsSuccess state)
					const currentSmsSuccess =
						newStatus === "sent" || newStatus === "verified"
							? true
							: smsSuccess;
					saveVerificationToStorage(
						verificationId,
						newStatus === "verified" ? null : shareableUrl,
						newStatus,
						currentSmsSuccess
					);
				}
			} catch (error) {
				// If no verification exists yet, that's okay - don't show error
				if (error.response?.status !== 404) {
					console.error("Error checking verification status:", error);
				}
			}
		}
	}, [buyer?.ID, dealerID, locationID]);

	// Bank Verification state
	const [linkToken, setLinkToken] = useState(null);
	const [bankLoading, setBankLoading] = useState(false);
	const [bankError, setBankError] = useState(null);
	const [bankAccount, setBankAccount] = useState(null);
	const [fundAmount, setFundAmount] = useState("");
	const [fundCheckResult, setFundCheckResult] = useState(null);
	const [fundCheckLoading, setFundCheckLoading] = useState(false);

	/**
	 * Starts the identity verification flow
	 */
	const handleStartIdentityVerification = async () => {
		setIdvLoading(true);
		setIdvError(null);

		try {
			// Debug: Log buyer and buyerAddress objects
			console.log("Buyer object:", buyer);
			console.log("Buyer Address object:", buyerAddress);

			// Validate required fields
			const missingFields = [];
			if (!buyer.firstName) missingFields.push("First Name");
			if (!buyer.lastName) missingFields.push("Last Name");
			if (!buyer.email) missingFields.push("Email");
			if (!buyer.cellPhone) missingFields.push("Phone Number");
			if (!buyer.birthDate) missingFields.push("Date of Birth");
			if (!buyerAddress?.street) missingFields.push("Street Address");
			if (!buyerAddress?.city) missingFields.push("City");
			if (!buyerAddress?.state) missingFields.push("State");
			if (!buyerAddress?.zip) missingFields.push("Zip Code");

			if (missingFields.length > 0) {
				setIdvError(
					`Buyer information is incomplete. Missing required fields: ${missingFields.join(
						", "
					)}. Please update the buyer's contact information before starting identity verification.`
				);
				setIdvLoading(false);
				return;
			}

			// Format phone number with + prefix if not already present
			let phoneNumber = buyer.cellPhone || "";
			if (phoneNumber && !phoneNumber.startsWith("+")) {
				phoneNumber = "+1" + phoneNumber.replace(/\D/g, ""); // Remove non-digits and add +1
			}

			// Create 8-digit dc_dealer_id by concatenating dealerID and locationID
			const dcDealerID =
				String(dealerID).padStart(4, "0") + String(locationID).padStart(4, "0");

			const contactData = {
				dc_dealer_id: dcDealerID,
				dc_contact_id: String(buyer.ID),
				user_id: uuid(), // Generate random UUID for user_id
				given_name: buyer.firstName,
				family_name: buyer.lastName,
				email: buyer.email,
				phone_number: phoneNumber,
				date_of_birth: buyer.birthDate,
				address: {
					street: buyerAddress.street,
					city: buyerAddress.city,
					region: buyerAddress.state,
					postal_code: buyerAddress.zip,
					country: "US",
				},
			};

			console.log("Sending identity verification request:", contactData);

			const response = await createIdentityVerification(contactData);

			console.log("Identity verification response:", response.data);

			// Handle response - check if shareable_url exists in response.data
			const responseData = response.data;
			if (responseData && responseData.shareable_url) {
				const verificationId = responseData.identity_verification_id;
				const url = responseData.shareable_url;
				// Use "active" status to trigger the "Send Verification" UI
				// The UI shows the send button when status is NOT "unverified"
				const status = responseData.status || "active";

				console.log("Setting verification data:", {
					verificationId,
					url,
					status,
				});

				setIdvUrl(url);
				setIdvVerificationId(verificationId);
				setIdvStatus(status);

				// Save to sessionStorage (SMS not sent yet)
				saveVerificationToStorage(verificationId, url, status, false);
			} else {
				console.error("Invalid response structure:", responseData);
				throw new Error("Failed to create identity verification session");
			}
		} catch (error) {
			console.error("Error starting identity verification:", error);
			setIdvError(
				error.response?.data?.message ||
					"Failed to start identity verification. Please try again."
			);
		} finally {
			setIdvLoading(false);
		}
	};

	/**
	 * Sends the verification URL via SMS to the buyer's phone
	 */
	const handleSendVerificationSms = async () => {
		if (!idvUrl || !buyer.cellPhone) {
			setIdvError("Cannot send SMS: Missing verification URL or phone number");
			return;
		}

		setSmsSending(true);
		setIdvError(null);
		setSmsSuccess(false);

		try {
			const user = JSON.parse(sessionStorage.getItem("user"));
			const dealerNumber = user?.messagingNumber;

			if (!dealerNumber) {
				throw new Error("Dealer messaging number not configured");
			}

			// Format phone number
			let phoneNumber = buyer.cellPhone.replace(/\D/g, "");

			const message = {
				fromNumber: "+1" + dealerNumber,
				recipients: ["+1" + phoneNumber],
				messageBody: `Hi ${buyer.firstName}, please complete your identity verification by clicking this link: ${idvUrl}`,
				customerID: buyer.ID,
				personType: "Customer",
				dealID: null,
				accountID: null,
				locationID: user?.locationID,
			};

			console.log("Sending verification SMS:", message);

			await sendTwilioMessage(message);
			setSmsSuccess(true);

			// Update status to 'sent' after SMS is successfully sent
			setIdvStatus("sent");
			saveVerificationToStorage(idvVerificationId, idvUrl, "sent", true);
		} catch (error) {
			console.error("Error sending verification SMS:", error);
			setIdvError(
				error.response?.data?.message ||
					"Failed to send verification link. Please try again."
			);
		} finally {
			setSmsSending(false);
		}
	};

	/**
	 * Checks the status of identity verification
	 */
	const handleCheckIdvStatus = async () => {
		setIdvLoading(true);
		setIdvError(null);

		try {
			// Create 8-digit dc_dealer_id by concatenating dealerID and locationID
			const dcDealerID =
				String(dealerID).padStart(4, "0") + String(locationID).padStart(4, "0");

			const response = await getIdentityVerificationStatusByContact(
				dcDealerID,
				String(buyer.ID)
			);

			if (response.data) {
				// Extract verification ID and URL from response
				const verificationId = response.data.identity_verification_id;
				const shareableUrl = response.data.shareable_url;

				// Update state if we got a verification ID
				if (verificationId && !idvVerificationId) {
					setIdvVerificationId(verificationId);
				}
				if (shareableUrl && !idvUrl) {
					setIdvUrl(shareableUrl);
				}

				// Check if kyc_check status is success, then mark as "verified"
				// Otherwise, use the actual API status (active, pending, etc.)
				const kycStatus = response.data.kyc_check?.status;
				const apiStatus = response.data.status;

				let newStatus;
				if (kycStatus === "success" && apiStatus === "success") {
					// Only mark as verified when KYC check is successful
					newStatus = "verified";
				} else if (apiStatus === "failed") {
					// Mark as failed if verification failed
					newStatus = "failed";
				} else {
					// For any other status without successful KYC (active, pending, etc.), use the actual API status
					// This ensures "active" stays "active" to show the "Send Verification" UI
					newStatus = apiStatus || "unverified";
				}

				setIdvStatus(newStatus);
				setIdvResults(response.data);

				if (newStatus === "verified") {
					setIdvUrl(null);
					setSmsSuccess(false);
					// Clear URL from storage when verification is complete
					saveVerificationToStorage(
						verificationId || idvVerificationId,
						null,
						newStatus,
						false
					);
				} else {
					// Update status in storage (preserve smsSuccess state)
					const currentSmsSuccess = newStatus === "sent" ? true : smsSuccess;
					saveVerificationToStorage(
						verificationId || idvVerificationId,
						shareableUrl || idvUrl,
						newStatus,
						currentSmsSuccess
					);
				}
			}
		} catch (error) {
			console.error("Error checking identity verification status:", error);
			setIdvError(
				error.response?.data?.message ||
					"Failed to check verification status. Please try again."
			);
		} finally {
			setIdvLoading(false);
		}
	};

	/**
	 * Handles sending bank linking URL to customer via SMS
	 */
	const handleSendBankLink = async () => {
		console.log("=== handleSendBankLink called ===");
		console.log("buyer:", buyer);
		console.log("dealerID:", dealerID);
		console.log("locationID:", locationID);

		setBankLinkSending(true);
		setBankLinkSuccess(false);

		try {
			// Create 8-digit dc_dealer_id
			const dcDealerID =
				String(dealerID).padStart(4, "0") + String(locationID).padStart(4, "0");
			console.log("dcDealerID:", dcDealerID);

			// Create shareable bank link
			console.log("Calling createShareableBankLink...");
			const response = await createShareableBankLink(dcDealerID, String(buyer.ID));
			console.log("createShareableBankLink response:", response);

			if (response.data?.shareable_url) {
				// Get dealer messaging number and customer phone
				const user = JSON.parse(sessionStorage.getItem("user"));
				console.log("User from sessionStorage:", user);
				const dealerNumber = user?.messagingNumber;
				console.log("Dealer messagingNumber:", dealerNumber);

				if (!dealerNumber) {
					alert("Dealer messaging number not configured. Please contact support.");
					return;
				}

				// Get phone number from buyer
				console.log("Buyer object:", buyer);
				console.log("buyer.cellPhone:", buyer.cellPhone);
				console.log("buyer.homePhone:", buyer.homePhone);
				console.log("buyer.workPhone:", buyer.workPhone);

				let phoneNumber = buyer.cellPhone || buyer.homePhone || buyer.workPhone || "";
				console.log("Initial phoneNumber:", phoneNumber);

				if (!phoneNumber) {
					console.error("No phone number found in buyer object!");
					alert("No phone number available for customer. Please add a phone number and try again.");
					return;
				}

				// Remove non-digits from phone number
				phoneNumber = phoneNumber.replace(/\D/g, "");
				console.log("Phone after removing non-digits:", phoneNumber);

				if (!phoneNumber) {
					console.error("Phone number is empty after removing non-digits!");
					alert("Invalid phone number format. Please update the customer's phone number.");
					return;
				}

				// Send SMS to customer
				const message = {
					fromNumber: "+1" + dealerNumber,
					recipients: ["+1" + phoneNumber],
					messageBody: `Hi ${buyer.firstName}, your identity is verified! Now link your bank account: ${response.data.shareable_url}`,
					customerID: buyer.ID,
					personType: "Customer",
					locationID: user?.locationID,
				};

				await sendTwilioMessage(message);
				setBankLinkSuccess(true);
			}
		} catch (error) {
			console.error("Error sending bank link:", error);
			alert(
				error.response?.data?.message ||
					"Failed to send bank link. Please try again."
			);
		} finally {
			setBankLinkSending(false);
		}
	};

	/**
	 * Initializes Plaid Link for bank account linking
	 */
	const handleStartBankVerification = async () => {
		setBankLoading(true);
		setBankError(null);

		try {
			const response = await createLinkToken(
				buyer.ID,
				buyer.email || `buyer_${buyer.ID}@dealerclick.com`
			);

			if (response.data && response.data.link_token) {
				setLinkToken(response.data.link_token);
			} else {
				throw new Error("Failed to create link token");
			}
		} catch (error) {
			console.error("Error creating link token:", error);
			setBankError(
				error.response?.data?.message ||
					"Failed to initialize bank linking. Please try again."
			);
			setBankLoading(false);
		}
	};

	/**
	 * Handles successful Plaid Link flow
	 */
	const onPlaidSuccess = useCallback(
		async (publicToken, metadata) => {
			setBankLoading(true);
			setBankError(null);

			try {
				// Exchange public token for access token
				const exchangeResponse = await exchangePublicToken(
					publicToken,
					buyer.ID
				);

				if (!exchangeResponse.data || !exchangeResponse.data.access_token) {
					throw new Error("Failed to exchange token");
				}

				const { access_token, item_id, accounts } = exchangeResponse.data;

				// Get the first account (or the selected account from metadata)
				const selectedAccount = accounts[0];

				// Create 8-digit dc_dealer_id by concatenating dealerID and locationID
				const dcDealerID =
					String(dealerID).padStart(4, "0") +
					String(locationID).padStart(4, "0");

				// Save bank verification to database
				const verificationData = {
					dc_dealer_id: dcDealerID,
					dc_contact_id: buyer.ID,
					user_id: buyer.ID,
					access_token: access_token,
					item_id: item_id,
					account_id: selectedAccount.account_id,
					account_name: selectedAccount.name,
					account_mask: selectedAccount.mask,
					account_type: selectedAccount.subtype || selectedAccount.type,
					institution_id:
						metadata.institution?.institution_id || "unknown_institution",
				};

				await saveBankVerification(verificationData);

				// Update UI with connected account
				setBankAccount({
					name: selectedAccount.name,
					mask: selectedAccount.mask,
					type: selectedAccount.subtype || selectedAccount.type,
					institution: metadata.institution?.name || "Bank",
				});

				// Reset link token so Plaid Link doesn't auto-open again
				setLinkToken(null);
			} catch (error) {
				console.error("Error saving bank verification:", error);
				setBankError(
					error.response?.data?.message ||
						"Failed to save bank account. Please try again."
				);
			} finally {
				setBankLoading(false);
			}
		},
		[buyer.ID, dealerID, locationID]
	);

	/**
	 * Handles Plaid Link errors or user exit
	 */
	const onPlaidExit = useCallback((err) => {
		if (err) {
			console.error("Plaid Link error:", err);
			setBankError("Bank linking was unsuccessful. Please try again.");
		}
		setLinkToken(null);
		setBankLoading(false);
	}, []);

	// Initialize Plaid Link
	const { open: openPlaidLink, ready: plaidReady } = usePlaidLink({
		token: linkToken,
		onSuccess: onPlaidSuccess,
		onExit: onPlaidExit,
	});

	// Auto-open Plaid Link when token is ready
	useEffect(() => {
		if (linkToken && plaidReady) {
			openPlaidLink();
		}
	}, [linkToken, plaidReady, openPlaidLink]);

	/**
	 * Checks funds for the connected bank account
	 */
	const handleCheckFunds = async () => {
		if (!fundAmount || parseFloat(fundAmount) <= 0) {
			setBankError("Please enter a valid amount");
			return;
		}

		setFundCheckLoading(true);
		setBankError(null);
		setFundCheckResult(null);

		try {
			const response = await checkFundsByContact(
				buyer.ID,
				parseFloat(fundAmount)
			);

			if (response.data) {
				setFundCheckResult(response.data);
			}
		} catch (error) {
			console.error("Error checking funds:", error);
			setBankError(
				error.response?.data?.message ||
					"Failed to check funds. Please try again."
			);
		} finally {
			setFundCheckLoading(false);
		}
	};

	// Show marketing message if dealership doesn't have Plaid premium feature
	if (!usePlaid) {
		return (
			<div className="plaid-modal-content">
				<Row>
					<Col xs="12">
						<Card>
							<CardHeader>
								<div className="d-flex align-items-center justify-content-between">
									<h5 className="card-title mb-0">
										Identity Verification (KYC)
									</h5>
									<img
										src={PlaidLogo}
										alt="Plaid"
										style={{ height: "30px", marginLeft: "auto" }}
									/>
								</div>
							</CardHeader>
							<CardBody>
								<Alert color="info">
									<h6 className="mb-3">
										<i className="fa fa-star mr-2" />
										Premium Feature
									</h6>
									<p className="mb-2">
										Plaid identity verification is a premium feature that allows
										you to:
									</p>
									<ul className="mb-3">
										<li>Verify customer identity using government-issued ID</li>
										<li>Conduct KYC (Know Your Customer) checks</li>
										<li>Link and verify bank accounts</li>
										<li>Check available funds</li>
									</ul>
									<p className="mb-0">
										<strong>
											Contact us to enable Plaid verification for your
											dealership.
										</strong>
									</p>
								</Alert>
							</CardBody>
						</Card>
					</Col>
				</Row>
			</div>
		);
	}

	return (
		<div className="plaid-modal-content">
			<Row>
				{/* Identity Verification Section */}
				<Col xs="12">
					<Card>
						<CardHeader>
							<div className="d-flex align-items-center justify-content-between">
								<h5 className="card-title mb-0">Identity Verification (KYC)</h5>
								<img
									src={PlaidLogo}
									alt="Plaid"
									style={{ height: "30px", marginLeft: "auto" }}
								/>
							</div>
						</CardHeader>
						<CardBody>
							{idvError && <Alert color="danger">{idvError}</Alert>}

							{(!idvStatus || idvStatus === "unverified") && (
								<div>
									<p>
										Verify {buyer.firstName} {buyer.lastName}&apos;s identity
										using government-issued ID.
									</p>
									<Button
										color="primary"
										onClick={handleStartIdentityVerification}
										disabled={idvLoading}
										block
									>
										{idvLoading ? (
											<>
												<i className="fa fa-spinner fa-spin mr-2" />
												Starting...
											</>
										) : (
											<>
												<i className="fa fa-play-circle mr-2" />
												Start Identity Verification
											</>
										)}
									</Button>
								</div>
							)}

							{idvStatus && idvStatus !== "unverified" && (
								<div>
									<div className="mb-3">
										<strong>Status: </strong>
										<Badge
											color={
												idvStatus === "verified"
													? "success"
													: idvStatus === "success"
													? "success"
													: idvStatus === "failed"
													? "danger"
													: idvStatus === "sent"
													? "info"
													: idvStatus === "active"
													? "warning"
													: idvStatus === "unverified"
													? "secondary"
													: "secondary"
											}
										>
											{idvStatus}
										</Badge>
									</div>

									{idvUrl && (
										<div className="mb-3">
											<Alert color="info">
												<strong>Action Required:</strong>
												<br />
												{smsSuccess ? (
													<span className="text-success">
														Verification link sent to {buyer.cellPhone}
													</span>
												) : (
													<>
														Send verification link to {buyer.firstName}&apos;s
														phone:
														<br />
														<Button
															color="info"
															size="sm"
															className="mt-2"
															onClick={handleSendVerificationSms}
															disabled={smsSending}
														>
															{smsSending ? (
																<>
																	<i className="fa fa-spinner fa-spin mr-2" />
																	Sending...
																</>
															) : (
																<>
																	<i className="fa fa-paper-plane mr-2" />
																	Send Verification
																</>
															)}
														</Button>
													</>
												)}
											</Alert>
										</div>
									)}

									{smsSuccess && (
										<Row>
											<Col xs="12" sm="6" className="mb-2 mb-sm-0">
												<Button
													color="info"
													onClick={handleSendVerificationSms}
													disabled={smsSending || !idvUrl}
													block
													size="sm"
												>
													{smsSending ? (
														<>
															<i className="fa fa-spinner fa-spin mr-2" />
															Resending...
														</>
													) : (
														<>
															<i className="fa fa-paper-plane mr-2" />
															Resend Verification
														</>
													)}
												</Button>
											</Col>
											<Col xs="12" sm="6">
												<Button
													color="secondary"
													onClick={handleCheckIdvStatus}
													disabled={
														idvLoading || smsSending || idvStatus === "verified"
													}
													block
													size="sm"
												>
													{idvLoading ? (
														<>
															<i className="fa fa-spinner fa-spin mr-2" />
															Checking...
														</>
													) : (
														<>
															<i className="fa fa-sync mr-2" />
															Check Status
														</>
													)}
												</Button>
											</Col>
										</Row>
									)}

									{idvResults?.verified_data && (
										<div className="mt-3">
											<h6>Verified Information:</h6>
											<pre className="bg-light p-2 small">
												{JSON.stringify(idvResults.verified_data, null, 2)}
											</pre>
										</div>
									)}

									{idvResults?.kyc_details && (
										<div className="mt-2">
											<h6>KYC Results:</h6>
											<pre className="bg-light p-2 small">
												{JSON.stringify(idvResults.kyc_details, null, 2)}
											</pre>
										</div>
									)}

									{/* Bank Account Linking Section - Show after IDV is verified */}
									{idvStatus === "verified" && (
										<>
											<hr />
											<h6>Step 2: Bank Account Linking</h6>
											<p className="text-muted small">
												Send a link to {buyer.firstName} to securely link their bank account.
											</p>

											{!bankLinkSuccess && (
												<Button
													color="primary"
													onClick={handleSendBankLink}
													disabled={bankLinkSending}
													block
												>
													{bankLinkSending ? "Sending..." : "Send Bank Link"}
												</Button>
											)}

											{bankLinkSuccess && (
												<Alert color="success" className="mb-0">
													<strong>Bank linking SMS sent!</strong>
													<br />
													Sent to {buyer.firstName}&apos;s phone.
													<Button
														color="link"
														size="sm"
														onClick={handleSendBankLink}
														className="p-0 ml-2"
													>
														Resend
													</Button>
												</Alert>
											)}
										</>
									)}
								</div>
							)}
						</CardBody>
					</Card>
				</Col>
			</Row>
		</div>
	);
};

PlaidModal.propTypes = {
	buyer: PropTypes.shape({
		ID: PropTypes.string.isRequired,
		firstName: PropTypes.string,
		lastName: PropTypes.string,
		email: PropTypes.string,
		cellPhone: PropTypes.string,
		birthDate: PropTypes.string,
	}).isRequired,
	buyerAddress: PropTypes.shape({
		street: PropTypes.string,
		city: PropTypes.string,
		state: PropTypes.string,
		zip: PropTypes.string,
	}),
	dealerID: PropTypes.string.isRequired,
	locationID: PropTypes.string.isRequired,
	usePlaid: PropTypes.bool.isRequired,
};

export default PlaidModal;
